import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';

const ComplianceTracker = ({ documents = [] }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);

  const complianceData = useMemo(() => {
    const total = documents.length || 1;
    const active = documents.filter(doc => doc.status?.name === 'Active' && !doc.deprecated).length;
    const pending = documents.filter(doc => doc.status?.name === 'Pending' || doc.status?.name === 'Review').length;
    const expired = documents.filter(doc => {
      if (!doc.updatedAt) return false;
      const monthsOld = (new Date() - new Date(doc.updatedAt)) / (1000 * 60 * 60 * 24 * 30);
      return monthsOld > 12;
    }).length;
    const draft = documents.filter(doc => doc.status?.name === 'Draft').length;

    return {
      total,
      active,
      pending,
      expired,
      draft,
      complianceRate: Math.round((active / total) * 100),
      pendingRate: Math.round((pending / total) * 100),
      expiredRate: Math.round((expired / total) * 100)
    };
  }, [documents]);

  const complianceSegments = [
    { label: 'Compliant', value: complianceData.active, percentage: complianceData.complianceRate, semantic: 'success', description: 'Active and up-to-date documents' },
    { label: 'Pending Review', value: complianceData.pending, percentage: complianceData.pendingRate, semantic: 'warning', description: 'Documents awaiting review or approval' },
    { label: 'Potentially Expired', value: complianceData.expired, percentage: complianceData.expiredRate, semantic: 'danger', description: 'Documents not updated in over 12 months' },
    { label: 'Draft', value: complianceData.draft, percentage: Math.round((complianceData.draft / complianceData.total) * 100), semantic: 'info', description: 'Documents in draft status' }
  ];

  const complianceScore = Math.round(
    (complianceData.complianceRate * 0.7) +
    (Math.max(0, 100 - complianceData.expiredRate) * 0.2) +
    (Math.max(0, 100 - complianceData.pendingRate) * 0.1)
  );

  const scoreSemantic = dash.scoreSemantic(complianceScore);

  const getScoreLabel = (score) => {
    if (score >= 95) return 'Excellent';
    if (score >= 85) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 50) return 'Poor';
    return 'Critical';
  };

  return (
    <div className={dash.widgetShell('h-full')}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-semibold ${theme.text.primary}`}>Compliance Tracker</h3>
        <div className={cn('flex items-center gap-2', dash.pill(scoreSemantic))}>
          <div className={cn('w-2 h-2 rounded-full', dash.dot(scoreSemantic))} />
          <span className="text-sm font-medium">{getScoreLabel(complianceScore)}</span>
        </div>
      </div>

      <div className="flex items-center justify-center mb-6">
        <div className={cn('relative', dash.text(scoreSemantic))}>
          <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              strokeWidth="8"
              className={cn('opacity-20 stroke-current', dash.isRefined && 'text-slate-200 dark:text-slate-700')}
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${complianceScore * 2.83} 283`}
              className={cn('transition-all duration-1000 ease-out stroke-current', dash.ringStroke(complianceScore))}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`text-2xl font-bold ${theme.text.primary}`}>{complianceScore}%</div>
              <div className={`text-xs ${theme.text.muted}`}>Score</div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {complianceSegments.map((segment, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn('w-3 h-3 rounded-full', dash.dot(segment.semantic))} />
                <span className={`text-sm font-medium ${theme.text.primary}`}>{segment.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${theme.text.secondary}`}>{segment.value}</span>
                <span className={`text-xs ${theme.text.muted} w-8 text-right`}>{segment.percentage}%</span>
              </div>
            </div>
            <div className={cn('w-full rounded-full h-1.5', dash.progressTrack)}>
              <div
                className={cn('h-1.5 rounded-full transition-all duration-500 ease-out', dash.fill(segment.semantic))}
                style={{ width: `${segment.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={`mt-6 pt-4 border-t ${theme.border.light}`}>
        <h4 className={`text-sm font-medium ${theme.text.primary} mb-2}`}>Recommendations</h4>
        <ul className="space-y-1">
          {complianceData.pending > 0 && (
            <li className={`text-xs ${theme.text.secondary} flex items-center gap-2`}>
              Review {complianceData.pending} pending documents
            </li>
          )}
          {complianceData.expired > 0 && (
            <li className={`text-xs ${theme.text.secondary} flex items-center gap-2`}>
              Update {complianceData.expired} expired documents
            </li>
          )}
          {complianceData.draft > 0 && (
            <li className={`text-xs ${theme.text.secondary} flex items-center gap-2`}>
              Finalize {complianceData.draft} draft documents
            </li>
          )}
          {complianceScore >= 95 && (
            <li className={cn('text-xs flex items-center gap-2', dash.text('success'))}>
              Excellent compliance! Keep it up.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ComplianceTracker;
